import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { UiPath } from '../components/UiPath';

type Row = { k: string; v: string };

const FIELD_TYPES: Row[] = [
  { k: 'Text', v: 'Free text, up to 2,000 characters.' },
  { k: 'Number', v: 'A whole or decimal number.' },
  { k: 'Currency amount', v: 'An amount of money, stored as a number.' },
  { k: 'Date', v: 'A calendar day with no time, written YYYY-MM-DD in a file or the API.' },
  { k: 'Choose one', v: 'One option from a list you write. Needs at least one option.' },
  { k: 'Choose several', v: 'Any number of options from the list. Needs at least one option.' },
  { k: 'Team member', v: 'A person in your workspace.' },
  { k: 'Web address', v: 'A link. It must start with http:// or https://.' },
  { k: 'Yes / no', v: 'A checkbox.' },
  {
    k: 'Location',
    v: 'A place picked from a search box, such as an address, a city or a country. It can be filtered by region (see below). It cannot be filled from a file.',
  },
];

const VISIBILITY: Row[] = [
  { k: 'Everyone with CRM', v: 'The default. Anyone who can use CRM sees and fills in the field.' },
  {
    k: 'Customer-success managers and team leads',
    v: 'People who hold the customer-success manager or team lead role.',
  },
  {
    k: 'Customer-success team leads only',
    v: 'People who hold the team lead role. A manager does not see it.',
  },
];

const LIMITS: Row[] = [
  { k: 'Fields', v: '50 per record type, retired fields included.' },
  { k: 'Field name', v: 'Up to 60 characters.' },
  { k: 'Options', v: 'Up to 50 per choice field, each name up to 60 characters.' },
  { k: 'Text value', v: 'Up to 2,000 characters.' },
  { k: 'Tags', v: 'A tag name is up to 40 characters. A record carries up to 25 tags.' },
];

const SURFACES: Row[] = [
  {
    k: 'Client form',
    v: 'Edit a client, or add a new company, and the tags and custom fields sit in their own section. The section does not appear until your workspace has defined at least one.',
  },
  {
    k: 'Client page',
    v: 'The details list shows the tags and the fields that have a value. Empty fields are left out, and so are fields the viewer may not see.',
  },
  {
    k: 'Contacts and deals',
    v: 'The create and edit panels for a contact and for a deal carry the fields defined for that record type. A deal keeps what was entered on it once it becomes a client.',
  },
  {
    k: 'Clients list',
    v: 'Every field is a column you can switch on in the Columns menu, and every field can be used as a filter beside tags. Field columns do not sort. A location field is filtered by region: a continent, a country, or a region your workspace defines.',
  },
  {
    k: 'Search',
    v: 'The Clients search and Cmd-K match text, web address and choice fields (a choice by its name). Numbers, dates, yes/no and team members are not searched, and neither is any field limited to a role.',
  },
  {
    k: 'Import',
    v: 'The spreadsheet import maps a column to each company field, and suggests a column whose heading matches the field name. Team member fields cannot be filled from a file.',
  },
  {
    k: 'Email',
    v: 'Write {{field.key}} in a template or an email to insert a client field. If the client has no value, or you may not see the field, it is left blank.',
  },
  {
    k: 'Merging clients',
    v: 'Fields the kept client has no value for are filled from the other one. Where both clients have a value, you pick which to keep in the merge preview. Tags are combined.',
  },
  {
    k: 'Revvly Assistant',
    v: 'Ask it to set or clear a client’s fields or tags by name. It shows a confirmation card first and applies the same rules as the form. It never creates a field or a tag.',
  },
  {
    k: 'Public API',
    v: 'The client query returns customFields, read-only. See the API reference.',
  },
];

const IMPORT_FORMS: Row[] = [
  { k: 'Yes / no', v: 'yes, y, true or 1 for yes. no, n, false or 0 for no.' },
  { k: 'Choose one', v: 'The option’s name or its key.' },
  { k: 'Choose several', v: 'Option names or keys, separated by ; or |.' },
  { k: 'Number and currency', v: 'A number. Commas are ignored, so 1,200 reads as 1200.' },
  { k: 'Date', v: 'YYYY-MM-DD.' },
  { k: 'Location', v: 'Not importable. Pick the place on the record afterwards.' },
  { k: 'Blank cell', v: 'Nothing is set. A blank never clears a value.' },
];

function Rows({ rows }: { rows: Row[] }) {
  return (
    <div className="dp-fields-table" style={{ marginTop: 18 }}>
      {rows.map((r) => (
        <div className="dp-field-row" key={r.k}>
          <div className="dp-field-k">{r.k}</div>
          <div className="dp-field-v">{r.v}</div>
        </div>
      ))}
    </div>
  );
}

export function CustomFieldsGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Add custom fields and tags — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Custom fields and tags</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Add <em>custom fields</em> and tags.
        </h1>
        <p className="dp-oneliner">
          Record what Revvly does not ask for on its own, such as an account tier, a renewal owner
          or a contract link. Admins define the fields once; everyone with CRM fills them in on the
          client, contact or deal. This page covers how to create one, where it shows, who sees it,
          and what happens when you retire it.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          Know what you are creating
        </h2>
        <p className="dp-plain">
          There are two things. A <strong>custom field</strong> is a named value with a type, such
          as Account tier (choose one) or Renewal owner (team member). It is defined for one record
          type: <strong>Clients</strong>, <strong>Contacts</strong> or <strong>Deals</strong>. A{' '}
          <strong>tag</strong> is a coloured label with no value, such as Strategic account. Tags
          are shared by all three record types, and a record can carry several.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Both need CRM. Defining them is for workspace admins and owners only. Anyone with CRM can
          then fill them in, except a field limited to a role (step 04).
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          Create a field
        </h2>
        <p className="dp-plain">
          Open the Custom fields screen and choose New field. Pick the record type, name the field,
          pick its type, and for a choice field write its options. Choose who can see it, then
          create it. The field is offered on that record type&rsquo;s forms straight away.
        </p>
        <UiPath
          steps={['Settings', 'Workspace', 'Custom fields']}
          note="The item appears for admins and owners in workspaces with CRM. Everyone else does not see it."
        />
        <Rows rows={FIELD_TYPES} />
        <ul className="dp-connects" style={{ marginTop: 18 }}>
          <li>
            Revvly makes the field&rsquo;s <strong>key</strong> from its name, for example{' '}
            <span className="api-inline">account_tier</span>. The key never changes, even if the
            name does, and a new field never reuses the key of a retired one. The key is what email
            placeholders, files and the API use.
          </li>
          <li>
            <strong>The type cannot be changed</strong> once the field exists. To use a different
            type, create a new field and retire the old one.
          </li>
          <li>
            <strong>Options cannot be removed</strong> once added, because a record may still hold
            one. Each option keeps its key for life, so a reworded name keeps working.
          </li>
        </ul>
        <Rows rows={LIMITS} />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          A <strong>location</strong> field stores the place you pick, and the filter matches on it.
          Continents and countries are built in and match on the place&rsquo;s country, so a client
          just across a border is never included. Workspace admins and owners can also define their
          own regions, each a mix of whole countries, boxes and circles, under Custom fields,
          Regions.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          Create a tag
        </h2>
        <p className="dp-plain">
          Tags live above the fields on the same screen. Type a name, pick a colour from the
          palette (six hues, gray, blue, teal, green, orange and red, each in four tints) and add
          it. Tag names are unique, and a colour can be changed later. Deleting a tag takes it off every client, contact and deal that carries it,
          so check how widely it is used first.
        </p>
        <UiPath steps={['Settings', 'Workspace', 'Custom fields', 'Tags']} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          Decide who sees a field
        </h2>
        <p className="dp-plain">
          By default a field is open to everyone with CRM. Limit it to a role when the value is
          sensitive, such as an internal health note. Admins and owners hold every role, so they
          always see it. Change the setting any time from the Who sees column on the Custom fields
          screen.
        </p>
        <Rows rows={VISIBILITY} />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          A limited field is hidden everywhere the value could leak: the forms, the client page, the
          columns, the email placeholders and the API. It is also kept out of search, so nobody can
          confirm a value by searching for it.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          Fill them in and find them again
        </h2>
        <p className="dp-plain">
          Fields and tags are set on the record&rsquo;s own form, not on the Custom fields screen.
          This is where they show up once your workspace has defined some.
        </p>
        <UiPath steps={['Clients', 'open a client', 'Edit']} />
        <Rows rows={SURFACES} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          Fill fields from a spreadsheet
        </h2>
        <p className="dp-plain">
          During the CRM import you map a column to each company field. Each cell is read the way
          you would write it by hand:
        </p>
        <Rows rows={IMPORT_FORMS} />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          A cell Revvly cannot read, such as an option that does not exist, names the field and the
          value so you can fix it. See <Link to="/docs/guides/crm">the CRM guide</Link> for the rest
          of the import.
        </p>
        <UiPath steps={['Import', 'Contacts and companies']} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">07</span>
          Retire a field you no longer use
        </h2>
        <p className="dp-plain">
          Choose Retire on the field&rsquo;s row. A retired field is no longer offered on forms and
          no longer accepts values, but nothing is deleted: the values already stored are kept.
          Switch on Show retired to list retired fields, and choose Restore to bring one back. A
          retired field still counts toward the limit of 50, and its key stays reserved.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">08</span>
          Read fields from the API
        </h2>
        <p className="dp-plain">
          Integrations read a client&rsquo;s fields through the public GraphQL API. Each entry has
          the field&rsquo;s key, name, type and value. The value is JSON-encoded in{' '}
          <span className="api-inline">valueJson</span>, and a choice holds option keys, not names.
          The list leaves out fields limited to a role. The public API only reads custom fields; set
          them in the app, from a file, or with the Assistant. See{' '}
          <Link to="/docs/api/graphql-public">the public GraphQL API reference</Link>.
        </p>
      </section>
    </div>
  );
}
